import React, { useMemo } from 'react';
import useGameSwitchQuery from '../hooks/useGameSwitchQuery';
import ServerStatus from '@/features/timeMachine/components/ServerStatus';
import { useNavigate } from 'react-router-dom';
import { LocalSessionState, useAuth } from '@/features/scaffolding/components/AuthContext';
import { isUUID } from 'validator';
import useCurrentGameId from '@/shared/hooks/useCurrentGameId';

const LoadingGame = () => {
  const gameId = useCurrentGameId();
  const auth = useAuth();
  const [loadingFlag, setLoadingFlag] = React.useState(true);
  const [errorFlag, setErrorFlag] = React.useState(true);
  const [showSessionCorruptedDialog, setShowSessionCorruptedDialog] = React.useState(false);
  const switchGameQuery = useGameSwitchQuery();
  const navigate = useNavigate();

  React.useEffect(() => {
    setLoadingFlag(['pending', 'idle'].includes(switchGameQuery.status));
    setErrorFlag(switchGameQuery.status === 'error');
    if (switchGameQuery.error)
      if (
        switchGameQuery.error?.name === 'SessionCorruptionError' ||
        switchGameQuery.error?.code === 'SESSION_CORRUPTED'
      ) {
        setShowSessionCorruptedDialog(true);
      }
  }, [switchGameQuery.status]);

  const handleCancel = () => {
    navigate('/games');
  };

  const isValid = useMemo(
    () => Boolean(gameId) && isUUID(gameId) && auth.sessionState === LocalSessionState.READY,
    [gameId, auth.sessionState]  );

  const sessionCorruptData = {
    title: 'Session Corrupted',
    message: 'Your session has become corrupted. Please log out and log back in.',
    confirmText: 'Log Out Now',
    onConfirm: async () => {
      await auth.handleSignOut();
    },
    retrybale: true,
  };

  return (
    <ServerStatus
      open={loadingFlag || errorFlag || showSessionCorruptedDialog}
      title={showSessionCorruptedDialog ? sessionCorruptData.title : 'Loading Game'}
      message={
        showSessionCorruptedDialog
          ? sessionCorruptData.message
          : errorFlag
            ? 'Error loading game.'
            : `Please wait while we set things up for you!`
      }
      onYes={showSessionCorruptedDialog ? sessionCorruptData.onConfirm : handleCancel}
      retryable={showSessionCorruptedDialog ? sessionCorruptData.retrybale : errorFlag}
      onRetry={async () => {
        if (showSessionCorruptedDialog) {
          await auth.handleSignOut();
          return;
        }

        if (isValid) switchGameQuery.refetch();
      }}
      retryButtonText={showSessionCorruptedDialog ? sessionCorruptData.confirmText : 'Retry'}
      onClose={() => {}}
      cancelButtonColor="secondary"
      cancelButtonText={errorFlag ? 'Back to Game Selection' : `Cancel`}
      requiresConfirmation={!errorFlag}
      cancellable={!showSessionCorruptedDialog}
    />
  );
};

export default LoadingGame;
